"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getForecastDataSchema = exports.updateForecastSchema = exports.createForecastSchema = exports.forecastSchema = exports.distributionValueSchema = exports.excludedRangeSchema = void 0;
const enums_1 = require("../enums");
const zod_1 = require("zod");
exports.excludedRangeSchema = zod_1.z
    .object({
    id: zod_1.z.coerce.number().optional(), // optional ID for existing records
    forecastId: zod_1.z.coerce.number().optional(), // optional ID for the associated forecast
    name: zod_1.z.string().min(1, 'Name is required'),
    dateFrom: zod_1.z.coerce.date(),
    dateTo: zod_1.z.coerce.date(),
    createdBy: zod_1.z.coerce.number().optional(),
    updatedBy: zod_1.z.coerce.number().optional(),
})
    .refine((data) => {
    // Only validate date range if both dates are valid
    if (!data.dateFrom || !data.dateTo)
        return true;
    return new Date(data.dateTo) >= new Date(data.dateFrom);
}, {
    message: 'End date must be after start date',
    path: ['dateTo'],
})
    .transform((data) => ({
    // Transform to ensure clean data structure
    ...data,
    name: data.name?.trim() || '',
}));
exports.distributionValueSchema = zod_1.z.object({
    id: zod_1.z.coerce.number().nullish(), // optional ID for existing records
    forecastId: zod_1.z.coerce.number().optional(), // optional ID for the associated forecast
    dayTimeSlotId: zod_1.z.coerce.number(),
    value: zod_1.z.coerce.number().nullable(),
});
const baseForecastSchema = zod_1.z.object({
    workingUnitId: zod_1.z.number().int().positive({
        message: 'Working Unit is required',
    }),
    operativePlannerId: zod_1.z.number().nullish(),
    strategicPlannerId: zod_1.z.number().nullish(),
    brandId: zod_1.z.coerce.number().optional(),
    customerSegmentId: zod_1.z.number().nullish(),
    useDistributions: zod_1.z.boolean().default(false),
    measures: zod_1.z.array(zod_1.z.string()).nullish(),
    regressors: zod_1.z.array(zod_1.z.coerce.number()).nullish(),
    excludedRanges: zod_1.z.array(exports.excludedRangeSchema).optional(),
    distributions: zod_1.z.array(exports.distributionValueSchema).optional(),
    startDate: zod_1.z.coerce.date().optional(),
    endDate: zod_1.z.coerce.date(),
    interval: zod_1.z.nativeEnum(enums_1.ForecastInterval).optional(),
    disableHolidayRules: zod_1.z.boolean().optional().default(false),
    createdBy: zod_1.z.number(),
    updatedBy: zod_1.z.number(),
});
const isValidDateRange = ({ startDate, endDate }) => {
    if (!startDate || !endDate)
        return true; // Skip validation if dates are not provided
    return new Date(endDate) > new Date(startDate);
};
const endDateInvalid = {
    message: 'End date must be after start date',
    path: ['endDate'],
};
const validateDateRangeRefine = [isValidDateRange, endDateInvalid];
exports.forecastSchema = baseForecastSchema.refine(...validateDateRangeRefine);
exports.createForecastSchema = baseForecastSchema
    .omit({
    createdBy: true,
    updatedBy: true,
})
    .refine(...validateDateRangeRefine);
exports.updateForecastSchema = baseForecastSchema
    .omit({
    createdBy: true,
    updatedBy: true,
})
    .extend({
    id: zod_1.z.coerce.number(),
})
    .refine(...validateDateRangeRefine);
// Define a flexible date input that accepts Date objects or ISO 8601 strings
// This is needed because frontend sends ISO strings via .toISOString()
const dateInput = zod_1.z.union([zod_1.z.coerce.date(), zod_1.z.string().datetime()]).optional();
exports.getForecastDataSchema = zod_1.z.object({
    groupBy: zod_1.z.nativeEnum(enums_1.ForecastGroupBy).optional(),
    valueType: zod_1.z.string().optional(),
    dateFrom: dateInput,
    dateTo: dateInput,
});
